<template>
  <v-container fluid class="pa-0 py-8">
    <div :style="{ width: $vuetify.display.xlAndUp ? '85%' : '95%' }" class="mx-auto">
      <!-- Header -->
      <div class="d-flex justify-space-between align-center mb-2 fade-in">
        <div>
          <p class="text-caption text-grey-darken-1 mb-1">Pipeline</p>
          <h1 class="page-title">Tasks &amp; Bugs</h1>
        </div>
        <div class="d-flex align-center" style="gap: 10px">
          <button
            class="bo"
            :class="{ 'bo-active': showArchive }"
            @click="toggleArchive"
          >
            <v-icon size="15">{{ showArchive ? 'mdi-archive' : 'mdi-archive-outline' }}</v-icon>
            {{ showArchive ? 'Aktive Aufgaben' : 'Archiv' }}
          </button>
          <button class="bp" @click="openCreateDialog">
            <v-icon size="16">mdi-plus</v-icon>
            Aufgabe hinzufügen
          </button>
        </div>
      </div>

      <v-divider class="my-6" style="border-color: #eceef2" />

      <!-- Filter & View Toggle -->
      <div class="d-flex align-center justify-space-between mb-5 toolbar">
        <div class="filter-row">
          <!-- Search -->
          <div class="search-wrap">
            <v-icon size="18" color="#888" class="search-icon">mdi-magnify</v-icon>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Aufgaben durchsuchen..."
              class="search-input"
            />
            <v-icon
              v-if="searchQuery"
              size="16"
              color="#888"
              class="search-clear"
              @click="searchQuery = ''"
            >mdi-close</v-icon>
          </div>

          <!-- Type Filter -->
          <FilterChip
            :options="typeOptions"
            v-model="typeFilter"
            label="Typ"
            :active="typeFilter !== 'all'"
            color="#5B5BD6"
          />

          <!-- Priority Filter -->
          <FilterChip
            :options="priorityOptions"
            v-model="priorityFilter"
            label="Priorität"
            :active="priorityFilter !== 'all'"
            color="#E65100"
          />

          <!-- Sort -->
          <FilterChip
            :options="sortOptions"
            v-model="sortMode"
            label="Sortieren"
            :active="sortMode !== 'newest'"
            color="#0a1628"
          />

          <button
            v-if="hasActiveFilters"
            class="btn-text-danger"
            @click="resetFilters"
          >
            Filter zurücksetzen
          </button>
        </div>

        <!-- View Toggle (im Archiv ausgeblendet, dort immer Liste) -->
        <div v-if="!showArchive" class="view-toggle">
          <button
            class="view-btn"
            :class="{ 'view-btn-active': viewMode === 'board' }"
            @click="setViewMode('board')"
          >
            <v-icon size="16">mdi-view-column-outline</v-icon>
            Board
          </button>
          <button
            class="view-btn"
            :class="{ 'view-btn-active': viewMode === 'list' }"
            @click="setViewMode('list')"
          >
            <v-icon size="16">mdi-format-list-bulleted</v-icon>
            Liste
          </button>
        </div>
      </div>

      <!-- Kanban Board (nicht im Archiv) -->
      <div v-if="effectiveViewMode === 'board'" class="kanban-board">
        <div
          v-for="col in statuses"
          :key="col.value"
          class="kanban-column"
          :class="{ 'drag-over': dragOverColumn === col.value }"
          @dragover.prevent
          @dragenter.prevent="dragOverColumn = col.value"
          @dragleave="onColumnLeave($event, col.value)"
          @drop="onDrop(col.value)"
        >
          <div class="column-header">
            <div class="d-flex align-center" style="gap: 8px;">
              <span class="status-dot" :style="{ background: col.dotColor }"></span>
              <span class="column-title">{{ col.label }}</span>
              <span class="count-chip">{{ tasksByStatus(col.value).length }}</span>
            </div>
          </div>

          <div class="cards-container">
            <template v-if="loading">
              <div v-for="n in 2" :key="`skel-${col.value}-${n}`" class="skel-card"></div>
            </template>

            <template v-else>
              <div
                v-for="task in tasksByStatus(col.value)"
                :key="task.id"
                class="task-card"
                :class="{
                  'task-card-bug': task.type === 'bug',
                  'task-card-dragging': draggingId === task.id,
                }"
                :draggable="!showArchive"
                @dragstart="!showArchive && onDragStart(task, $event)"
                @dragend="onDragEnd"
                @click="openEditDialog(task)"
              >
                <!-- Top: Ticket-Badge, Type, Priority -->
                <div class="task-card-top">
                  <span class="task-ticket-badge">{{ ticketNumber(task) }}</span>
                  <span class="task-type-tag" :class="`type-${task.type}`">
                    <v-icon size="10">{{ task.type === 'bug' ? 'mdi-bug-outline' : 'mdi-check-circle-outline' }}</v-icon>
                    {{ task.type === 'bug' ? 'Bug' : 'Task' }}
                  </span>
                  <span
                    class="task-prio-icon"
                    :class="`prio-${task.priority}`"
                    :title="`Priorität: ${priorityLabel(task.priority)}`"
                  >
                    <v-icon size="12">{{ priorityIcon(task.priority) }}</v-icon>
                  </span>
                </div>

                <!-- Title -->
                <h4 class="task-card-title">{{ task.title }}</h4>

                <!-- Description preview (max. 2 Zeilen, dann …) -->
                <p v-if="descriptionPreview(task.description)" class="task-card-desc">
                  {{ descriptionPreview(task.description) }}
                </p>

                <!-- Section reference -->
                <a
                  v-if="task.section"
                  class="task-section-ref"
                  @click.stop="goToSection(task.section.id)"
                  :title="task.section.handle"
                >
                  <img
                    v-if="task.section.imageUrl"
                    :src="task.section.imageUrl"
                    class="task-section-thumb"
                    @error="onAvatarError"
                  />
                  <div v-else class="task-section-thumb task-section-thumb-empty">
                    <v-icon size="9" color="#c5cbd3">mdi-image-off-outline</v-icon>
                  </div>
                  <span class="task-section-title">{{ task.section.title }}</span>
                </a>

                <!-- Deadline (oberhalb des Footers, falls gesetzt) -->
                <div
                  v-if="task.deadline"
                  class="task-deadline"
                  :class="{ 'task-deadline-overdue': isOverdue(task.deadline) }"
                  :title="isOverdue(task.deadline) ? 'Überfällig' : 'Deadline'"
                >
                  <v-icon size="11">mdi-calendar-outline</v-icon>
                  {{ formatDate(task.deadline) }}
                </div>

                <!-- Divider + Footer mit Assignee-Name -->
                <div class="task-card-divider"></div>
                <div class="task-card-foot">
                  <div class="task-assignee-row" v-if="task.assignee">
                    <img
                      v-if="task.assignee.avatar"
                      :src="task.assignee.avatar"
                      class="avatar-sm"
                      @error="onAvatarError"
                    />
                    <span v-else class="avatar-sm avatar-fallback">{{ initials(task.assignee) }}</span>
                    <span class="task-assignee-name">{{ task.assignee.firstname }} {{ task.assignee.lastname }}</span>
                  </div>
                  <div class="task-assignee-row task-assignee-empty" v-else>
                    <div class="avatar-sm avatar-empty">
                      <v-icon size="11" color="#c5cbd3">mdi-account-outline</v-icon>
                    </div>
                    <span class="task-assignee-name">Niemand zugewiesen</span>
                  </div>

                  <div class="task-card-actions" @click.stop>
                    <button
                      v-if="!showArchive && canEdit(task)"
                      class="task-icon-btn"
                      @click="openEditDialog(task)"
                      title="Bearbeiten"
                    >
                      <v-icon size="13">mdi-pencil-outline</v-icon>
                    </button>
                    <button
                      v-if="showArchive && canEdit(task)"
                      class="task-icon-btn"
                      @click="unarchiveTask(task)"
                      title="Wiederherstellen"
                    >
                      <v-icon size="13">mdi-archive-arrow-up-outline</v-icon>
                    </button>
                    <button
                      v-if="canEdit(task)"
                      class="task-icon-btn task-icon-btn-danger"
                      @click="confirmDelete(task)"
                      title="Löschen"
                    >
                      <v-icon size="13">mdi-delete-outline</v-icon>
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="tasksByStatus(col.value).length === 0 && dragOverColumn !== col.value" class="empty-column">
                <v-icon size="26" color="#c5cbd3">mdi-tray-outline</v-icon>
                <span>Keine Einträge</span>
              </div>

              <!-- Drop-Slot am Ende der Spalte – Höhe = Höhe der ziehenden Karte -->
              <div
                v-if="dragOverColumn === col.value && draggingId !== null && !isDraggingInThisColumn(col.value)"
                class="drop-slot"
                :style="{ height: draggingHeight + 'px' }"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="list-view">
        <table class="tasks-table">
          <thead>
            <tr>
              <th class="th-id">Ticket</th>
              <th class="th-title">Titel</th>
              <th class="th-assignee">Zuständig</th>
              <th class="th-status">Status</th>
              <th class="th-prio">Priorität</th>
              <th class="th-date">Deadline</th>
              <th class="th-date">Erstellt</th>
              <th class="th-actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="row-empty">Lade …</td>
            </tr>
            <tr v-else-if="filteredTasks.length === 0">
              <td colspan="8" class="row-empty">Keine Einträge</td>
            </tr>
            <tr
              v-for="task in filteredTasks"
              :key="task.id"
              class="tasks-table-row"
              :class="{ 'tasks-table-row-bug': task.type === 'bug' }"
              @click="openEditDialog(task)"
            >
              <td>
                <div class="row-id-cell">
                  <span class="task-ticket-badge">{{ ticketNumber(task) }}</span>
                  <span class="task-type-tag" :class="`type-${task.type}`">
                    <v-icon size="10">{{ task.type === 'bug' ? 'mdi-bug-outline' : 'mdi-check-circle-outline' }}</v-icon>
                    {{ task.type === 'bug' ? 'Bug' : 'Task' }}
                  </span>
                </div>
              </td>
              <td>
                <div class="row-title">{{ task.title }}</div>
                <div v-if="task.section" class="row-section-ref" @click.stop="goToSection(task.section.id)">
                  <img
                    v-if="task.section.imageUrl"
                    :src="task.section.imageUrl"
                    class="row-section-thumb"
                    @error="onAvatarError"
                  />
                  <v-icon v-else size="10" color="#5B5BD6">mdi-vector-intersection</v-icon>
                  <span>{{ task.section.title }}</span>
                </div>
              </td>
              <td>
                <div v-if="task.assignee" class="row-assignee" :title="`${task.assignee.firstname} ${task.assignee.lastname}`">
                  <img
                    v-if="task.assignee.avatar"
                    :src="task.assignee.avatar"
                    class="avatar-sm"
                    @error="onAvatarError"
                  />
                  <span v-else class="avatar-sm avatar-fallback">{{ initials(task.assignee) }}</span>
                  <span class="row-assignee-name">{{ task.assignee.firstname }} {{ task.assignee.lastname }}</span>
                </div>
                <span v-else class="row-muted">—</span>
              </td>
              <td>
                <span class="row-status-pill" :style="statusStyle(task.status)">
                  <span class="status-dot" :style="{ background: statusColorOf(task.status) }"></span>
                  {{ statusLabel(task.status) }}
                </span>
              </td>
              <td>
                <span class="row-prio" :class="`prio-${task.priority}`">
                  <v-icon size="11">{{ priorityIcon(task.priority) }}</v-icon>
                  {{ priorityLabel(task.priority) }}
                </span>
              </td>
              <td>
                <span v-if="task.deadline" class="row-date" :class="{ 'row-date-overdue': isOverdue(task.deadline) }">
                  <v-icon size="11">mdi-calendar-outline</v-icon>
                  {{ formatDate(task.deadline) }}
                </span>
                <span v-else class="row-muted">—</span>
              </td>
              <td>
                <span class="row-date row-date-muted">{{ formatDate(task.createdAt) }}</span>
              </td>
              <td>
                <div class="row-actions" @click.stop>
                  <button
                    v-if="!showArchive && canEdit(task)"
                    class="task-icon-btn"
                    @click="openEditDialog(task)"
                    title="Bearbeiten"
                  >
                    <v-icon size="14">mdi-pencil-outline</v-icon>
                  </button>
                  <button
                    v-if="showArchive && canEdit(task)"
                    class="task-icon-btn"
                    @click="unarchiveTask(task)"
                    title="Wiederherstellen"
                  >
                    <v-icon size="14">mdi-archive-arrow-up-outline</v-icon>
                  </button>
                  <button
                    v-if="canEdit(task)"
                    class="task-icon-btn task-icon-btn-danger"
                    @click="confirmDelete(task)"
                    title="Löschen"
                  >
                    <v-icon size="14">mdi-delete-outline</v-icon>
                  </button>
                  <span v-if="!canEdit(task)" class="row-muted">—</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create / Edit Dialog -->
    <div v-if="dialog" class="dialog-backdrop" @click.self="!saving && closeDialog()"></div>
    <v-dialog v-model="dialog" max-width="880" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="closeDialog"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon">
            <v-icon size="22" color="#0a1628">{{ form.type === 'bug' ? 'mdi-bug-outline' : 'mdi-check-circle-outline' }}</v-icon>
          </div>
          <div>
            <div class="dh-title">
              {{ readOnly ? 'Aufgabe ansehen' : (editMode ? 'Aufgabe bearbeiten' : 'Neue Aufgabe') }}
            </div>
            <div class="dh-sub">
              <template v-if="readOnly">
                Nur der Autor darf diese Aufgabe bearbeiten. Verschieben funktioniert für alle.
              </template>
              <template v-else-if="editMode">
                Aktualisiere die Details dieser Aufgabe.
              </template>
              <template v-else>
                Lege eine neue Task oder Bug-Meldung an.
              </template>
            </div>
          </div>
        </div>

        <div class="db" :class="{ 'db-readonly': readOnly }">
          <!-- Type Toggle -->
          <div class="field-block">
            <div class="field-label">Typ</div>
            <div class="type-toggle">
              <button
                class="type-toggle-btn"
                :class="{ 'type-active': form.type === 'task' }"
                @click="form.type = 'task'"
              >
                <v-icon size="14">mdi-check-circle-outline</v-icon>
                Task
              </button>
              <button
                class="type-toggle-btn"
                :class="{ 'type-active-bug': form.type === 'bug' }"
                @click="form.type = 'bug'"
              >
                <v-icon size="14">mdi-bug-outline</v-icon>
                Bug
              </button>
            </div>
          </div>

          <!-- Title -->
          <div class="field-block">
            <label class="field-label">Titel *</label>
            <input
              v-model="form.title"
              type="text"
              class="text-input"
              :class="{ 'input-error': submitted && !form.title }"
              placeholder="Kurze, klare Beschreibung"
            />
          </div>

          <!-- Description (Rich-Text) -->
          <div class="field-block">
            <label class="field-label">Beschreibung</label>
            <!-- Read-Only: HTML direkt rendern, kein Quill-Editor (verhindert Render-Doppelungen) -->
            <div v-if="readOnly" class="readonly-content">
              <div v-if="form.description" v-html="form.description"></div>
              <div v-else class="readonly-empty">Keine Beschreibung hinterlegt.</div>
            </div>
            <div v-else class="rich-editor-wrap">
              <QuillEditor
                ref="descEditor"
                v-model:content="form.description"
                content-type="html"
                theme="snow"
                placeholder="Mehr Kontext, Steps to reproduce, Akzeptanzkriterien…"
                :toolbar="quillToolbar"
                style="min-height: 180px"
              />
            </div>
          </div>

          <!-- Assignee + Priority Row -->
          <div class="field-row-2">
            <div class="field-block">
              <label class="field-label">Zuständig</label>
              <div class="combo-wrap" ref="assigneeDropdown">
                <button type="button" class="iw iw-trigger" :class="{ 'iw-f': assigneeMenuOpen }" @click="assigneeMenuOpen = !assigneeMenuOpen">
                  <v-icon v-if="!selectedAssignee" size="15" color="rgba(0,0,0,0.3)">mdi-account-outline</v-icon>
                  <img v-else-if="selectedAssignee.avatar" :src="selectedAssignee.avatar" class="trigger-avatar" @error="onAvatarError" />
                  <span v-else class="trigger-avatar-fallback">{{ initials(selectedAssignee) }}</span>
                  <span class="ii-val">
                    {{ selectedAssignee ? `${selectedAssignee.firstname} ${selectedAssignee.lastname}` : 'Niemand zugewiesen' }}
                  </span>
                  <v-icon size="14" color="#888" :class="{ 'chev-open': assigneeMenuOpen }">mdi-chevron-down</v-icon>
                </button>
                <div v-if="assigneeMenuOpen" class="combo-menu">
                  <button type="button" class="combo-option" @click="selectAssignee(null)">
                    <v-icon size="14" color="#c5cbd3">mdi-account-question-outline</v-icon>
                    <span class="combo-label">Niemand</span>
                  </button>
                  <div class="combo-divider"></div>
                  <button
                    v-for="emp in employees"
                    :key="emp.id"
                    type="button"
                    class="combo-option"
                    @click="selectAssignee(emp)"
                  >
                    <img v-if="emp.avatar" :src="emp.avatar" class="combo-avatar" @error="onAvatarError" />
                    <span v-else class="combo-avatar-fallback">{{ initials(emp) }}</span>
                    <span class="combo-label">{{ emp.firstname }} {{ emp.lastname }}</span>
                    <v-icon v-if="form.assigneeId === emp.id" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
                  </button>
                </div>
              </div>
            </div>

            <div class="field-block">
              <label class="field-label">Priorität</label>
              <div class="priority-grid">
                <button
                  v-for="p in priorities"
                  :key="p.value"
                  class="priority-option"
                  :class="{ 'priority-active': form.priority === p.value }"
                  :style="form.priority === p.value ? { borderColor: p.color, background: p.bgColor } : {}"
                  @click="form.priority = p.value"
                >
                  <v-icon size="13" :color="p.color">{{ p.icon }}</v-icon>
                  <span :style="form.priority === p.value ? { color: p.color } : {}">{{ p.label }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Deadline + Status (status only in edit) -->
          <div class="field-row-2">
            <div class="field-block">
              <label class="field-label">Deadline</label>
              <input
                v-model="form.deadline"
                type="text"
                class="text-input"
                placeholder="TT.MM.JJJJ"
              />
            </div>

            <div v-if="editMode" class="field-block">
              <label class="field-label">Status</label>
              <div class="combo-wrap" ref="statusDropdown">
                <button type="button" class="iw iw-trigger" :class="{ 'iw-f': statusMenuOpen }" @click="statusMenuOpen = !statusMenuOpen">
                  <span class="status-dot" :style="{ background: statusColorOf(form.status) }"></span>
                  <span class="ii-val">{{ statusLabel(form.status) }}</span>
                  <v-icon size="14" color="#888" :class="{ 'chev-open': statusMenuOpen }">mdi-chevron-down</v-icon>
                </button>
                <div v-if="statusMenuOpen" class="combo-menu">
                  <button
                    v-for="s in statuses"
                    :key="s.value"
                    type="button"
                    class="combo-option"
                    @click="selectStatus(s.value)"
                  >
                    <span class="status-dot" :style="{ background: s.dotColor }"></span>
                    <span class="combo-label">{{ s.label }}</span>
                    <v-icon v-if="form.status === s.value" size="14" color="#2E7D32" style="margin-left: auto">mdi-check</v-icon>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Section-Referenz -->
          <div class="field-block">
            <label class="field-label">Verknüpfte Section <span class="text-grey ml-1">(optional)</span></label>
            <div class="combo-wrap" ref="sectionDropdown">
              <button type="button" class="iw iw-trigger" :class="{ 'iw-f': sectionMenuOpen }" @click="sectionMenuOpen = !sectionMenuOpen">
                <template v-if="selectedSection">
                  <img
                    v-if="selectedSection.imageUrl"
                    :src="selectedSection.imageUrl"
                    class="trigger-section-thumb"
                    @error="onAvatarError"
                  />
                  <div v-else class="trigger-section-thumb trigger-section-thumb-empty">
                    <v-icon size="13" color="#c5cbd3">mdi-image-off-outline</v-icon>
                  </div>
                  <span class="ii-val">
                    <span class="trigger-section-title">{{ selectedSection.title }}</span>
                    <span class="trigger-section-handle">{{ selectedSection.handle }}</span>
                  </span>
                </template>
                <template v-else>
                  <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-vector-intersection</v-icon>
                  <span class="ii-val">Keine Section verknüpft</span>
                </template>
                <button
                  v-if="selectedSection"
                  type="button"
                  class="combo-clear"
                  title="Verknüpfung entfernen"
                  @click.stop="selectSection(null)"
                >
                  <v-icon size="14">mdi-close</v-icon>
                </button>
                <v-icon size="14" color="#888" :class="{ 'chev-open': sectionMenuOpen }">mdi-chevron-down</v-icon>
              </button>
              <div v-if="sectionMenuOpen" class="combo-menu combo-menu-section">
                <div class="combo-search">
                  <v-icon size="14" color="#888">mdi-magnify</v-icon>
                  <input
                    v-model="sectionSearch"
                    type="text"
                    placeholder="Section suchen..."
                    class="combo-search-input"
                    @click.stop
                  />
                </div>
                <div class="combo-divider"></div>
                <button type="button" class="combo-option" @click="selectSection(null)">
                  <v-icon size="14" color="#c5cbd3">mdi-close-circle-outline</v-icon>
                  <span class="combo-label">Keine Section</span>
                </button>
                <div v-if="filteredSections.length > 0" class="combo-divider"></div>
                <button
                  v-for="section in filteredSections"
                  :key="section.id"
                  type="button"
                  class="combo-option combo-option-section"
                  @click="selectSection(section)"
                >
                  <img
                    v-if="section.imageUrl"
                    :src="section.imageUrl"
                    class="combo-section-thumb"
                    @error="onAvatarError"
                  />
                  <div v-else class="combo-section-thumb combo-section-thumb-empty">
                    <v-icon size="13" color="#c5cbd3">mdi-image-off-outline</v-icon>
                  </div>
                  <div class="combo-section-text">
                    <div class="combo-label">{{ section.title }}</div>
                    <div class="combo-handle">{{ section.handle }}</div>
                  </div>
                  <v-icon v-if="form.sectionId === section.id" size="14" color="#2E7D32">mdi-check</v-icon>
                </button>
                <div v-if="filteredSections.length === 0" class="combo-empty">
                  Keine Section gefunden
                </div>
              </div>
            </div>
          </div>

          <!-- Author Info (read-only, edit mode) -->
          <div v-if="editMode && form.author" class="meta-info">
            <v-icon size="14" color="#888">mdi-account-edit-outline</v-icon>
            Erstellt von {{ form.author.firstname }} {{ form.author.lastname }} am {{ formatDate(form.createdAt) }}
          </div>

          <div v-if="formError" class="alert-error mt-3">
            <v-icon size="14" color="#D32F2F">mdi-alert-circle-outline</v-icon>
            {{ formError }}
          </div>
        </div>

        <div class="dff">
          <button class="bo" @click="closeDialog" :disabled="saving">
            {{ readOnly ? 'Schließen' : 'Abbrechen' }}
          </button>
          <button v-if="!readOnly" class="bp" @click="saveTask" :disabled="saving">
            <v-progress-circular v-if="saving" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">{{ editMode ? 'mdi-content-save' : 'mdi-plus' }}</v-icon>
            {{ editMode ? 'Speichern' : 'Anlegen' }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <div v-if="deleteDialog" class="dialog-backdrop" @click.self="!deleting && (deleteDialog = false)"></div>
    <v-dialog v-model="deleteDialog" max-width="440" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" @click="deleteDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(211,47,47,0.1)">
            <v-icon size="22" color="#D32F2F">mdi-delete-outline</v-icon>
          </div>
          <div>
            <div class="dh-title">Aufgabe löschen?</div>
            <div class="dh-sub">Diese Aktion kann nicht rückgängig gemacht werden.</div>
          </div>
        </div>
        <div class="db">
          <p class="text-body-2" style="color: #0a1628">
            <strong>{{ deleteTarget?.title }}</strong> wird unwiderruflich entfernt.
          </p>
        </div>
        <div class="dff">
          <button class="bo" @click="deleteDialog = false" :disabled="deleting">Abbrechen</button>
          <button class="bp btn-danger" @click="executeDelete" :disabled="deleting">
            <v-progress-circular v-if="deleting" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-delete-outline</v-icon>
            Löschen
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Toast Stack (rechts unten) -->
    <div class="toast-stack">
      <transition-group name="toast">
        <div
          v-for="t in toasts"
          :key="t.id"
          class="toast"
          :class="{ 'toast-error': t.type === 'error' }"
          @click="dismissToast(t.id)"
        >
          <template v-if="t.type === 'status'">
            <div class="toast-icon">
              <v-icon size="16" color="white">mdi-swap-horizontal</v-icon>
            </div>
            <div class="toast-body">
              <div class="toast-title">Status geändert</div>
              <div v-if="t.title" class="toast-task-title">{{ t.title }}</div>
              <div class="toast-flow">
                <span class="toast-status" :style="{ color: statusColorOf(t.fromStatus) }">
                  <span class="status-dot" :style="{ background: statusColorOf(t.fromStatus) }"></span>
                  {{ statusLabel(t.fromStatus) }}
                </span>
                <v-icon size="14" color="#888">mdi-arrow-right</v-icon>
                <span class="toast-status" :style="{ color: statusColorOf(t.toStatus) }">
                  <span class="status-dot" :style="{ background: statusColorOf(t.toStatus) }"></span>
                  {{ statusLabel(t.toStatus) }}
                </span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="toast-icon toast-icon-error">
              <v-icon size="16" color="white">mdi-alert-circle-outline</v-icon>
            </div>
            <div class="toast-body">
              <div class="toast-title">Fehler</div>
              <div class="toast-task-title">{{ t.message }}</div>
            </div>
          </template>
          <button class="toast-close" @click.stop="dismissToast(t.id)">
            <v-icon size="14">mdi-close</v-icon>
          </button>
        </div>
      </transition-group>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import FilterChip from "./FilterChip.vue";

const STATUSES = [
  { value: "todo",        label: "Offen",                 dotColor: "#9ca3af" },
  { value: "in_progress", label: "In Bearbeitung",        dotColor: "#3b82f6" },
  { value: "wont_fix",    label: "Wird nicht umgesetzt",  dotColor: "#6b7280" },
  { value: "done",        label: "Erledigt",              dotColor: "#10b981" },
];
const PRIORITIES = [
  { value: "low",    label: "Niedrig", icon: "mdi-arrow-down",      color: "#6b7280", bgColor: "rgba(107,114,128,0.08)" },
  { value: "medium", label: "Mittel",  icon: "mdi-minus",            color: "#3b82f6", bgColor: "rgba(59,130,246,0.08)" },
  { value: "high",   label: "Hoch",    icon: "mdi-arrow-up",         color: "#f59e0b", bgColor: "rgba(245,158,11,0.08)" },
  { value: "urgent", label: "Dringend",icon: "mdi-alert-octagon",    color: "#dc2626", bgColor: "rgba(220,38,38,0.08)" },
];

export default {
  name: "TasksOverview",
  components: { FilterChip },

  data() {
    return {
      tasks: [],
      employees: [],
      sections: [],
      sectionMenuOpen: false,
      sectionSearch: "",
      loading: true,
      saving: false,
      deleting: false,

      // View
      viewMode: localStorage.getItem("tasks.viewMode") || "board",
      showArchive: false,

      // Filters
      searchQuery: "",
      typeFilter: "all",
      priorityFilter: "all",
      sortMode: "newest",

      // Drag & drop
      draggingId: null,
      draggingHeight: 0,
      dragOverColumn: null,

      // Dialogs
      dialog: false,
      editMode: false,
      readOnly: false,
      submitted: false,
      formError: "",
      form: this.emptyForm(),
      assigneeMenuOpen: false,
      statusMenuOpen: false,

      deleteDialog: false,
      deleteTarget: null,

      // Toast (Status-Änderungen)
      toasts: [],
      toastSeq: 0,

      // constants
      statuses: STATUSES,
      priorities: PRIORITIES,
      typeOptions: [
        { value: "all",  label: "Alle" },
        { value: "task", label: "Tasks" },
        { value: "bug",  label: "Bugs" },
      ],
      priorityOptions: [
        { value: "all",    label: "Alle" },
        { value: "urgent", label: "Dringend" },
        { value: "high",   label: "Hoch" },
        { value: "medium", label: "Mittel" },
        { value: "low",    label: "Niedrig" },
      ],
      sortOptions: [
        { value: "newest",   label: "Neueste zuerst" },
        { value: "oldest",   label: "Älteste zuerst" },
        { value: "deadline", label: "Deadline (früh → spät)" },
        { value: "priority", label: "Priorität (hoch → niedrig)" },
      ],

      quillToolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ["small", false, "large", "huge"] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "code-block"],
        ["link"],
        ["clean"],
      ],
    };
  },

  computed: {
    effectiveViewMode() {
      // Im Archiv erzwingen wir die Listen-Ansicht – Kanban macht hier keinen Sinn
      return this.showArchive ? "list" : this.viewMode;
    },
    selectedAssignee() {
      if (!this.form.assigneeId) return null;
      return this.employees.find((e) => e.id === this.form.assigneeId) || null;
    },
    selectedSection() {
      if (!this.form.sectionId) return null;
      return this.sections.find((s) => s.id === this.form.sectionId) || null;
    },
    filteredSections() {
      const search = (this.sectionSearch || "").trim().toLowerCase();
      if (!search) return this.sections;
      return this.sections.filter(
        (s) =>
          (s.title || "").toLowerCase().includes(search) ||
          (s.handle || "").toLowerCase().includes(search)
      );
    },

    hasActiveFilters() {
      return this.typeFilter !== "all" || this.priorityFilter !== "all" || this.sortMode !== "newest";
    },

    filteredTasks() {
      let list = this.tasks.slice();
      if (this.typeFilter !== "all") list = list.filter((t) => t.type === this.typeFilter);
      if (this.priorityFilter !== "all") list = list.filter((t) => t.priority === this.priorityFilter);
      const search = this.searchQuery.trim().toLowerCase();
      if (search) {
        list = list.filter((t) =>
          (t.title || "").toLowerCase().includes(search) ||
          (t.description || "").toLowerCase().includes(search)
        );
      }
      list.sort((a, b) => {
        if (this.sortMode === "oldest") return new Date(a.createdAt) - new Date(b.createdAt);
        if (this.sortMode === "deadline") {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return new Date(a.deadline) - new Date(b.deadline);
        }
        if (this.sortMode === "priority") {
          const order = { urgent: 0, high: 1, medium: 2, low: 3 };
          return order[a.priority] - order[b.priority];
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
      return list;
    },
  },

  mounted() {
    this.loadAll();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },

  methods: {
    emptyForm() {
      return {
        id: null,
        type: "task",
        title: "",
        description: "",
        status: "todo",
        priority: "medium",
        assigneeId: null,
        sectionId: null,
        deadline: "",
        author: null,
        createdAt: null,
      };
    },

    async loadAll() {
      this.loading = true;
      try {
        const [tasksRes, empRes, sectionsRes] = await Promise.all([
          axios.get("/api/tasks/get", {
            params: { archived: this.showArchive ? "1" : "0" },
          }),
          axios.get("/api/employees/get"),
          axios.get("/api/sections/get", { params: { includeDrafts: "true" } }),
        ]);
        this.tasks = tasksRes.data.data || [];
        this.employees = (empRes.data || []).map((e) => ({
          id: e.id,
          firstname: e.firstname,
          lastname: e.lastname,
          avatar: `/api/employees/${e.id}/image`,
        }));
        this.sections = (sectionsRes.data || []).map((s) => ({
          id: s.id,
          title: s.title,
          handle: s.handle,
          hasImage: !!s.hasImage,
          imageUrl: s.hasImage
            ? `/api/sections/${s.id}/image?v=${new Date(s.updatedAt).getTime()}`
            : null,
        }));
      } catch (e) {
        console.error("Fehler beim Laden:", e);
      } finally {
        this.loading = false;
      }
    },

    tasksByStatus(status) {
      return this.filteredTasks.filter((t) => t.status === status);
    },

    isDraggingInThisColumn(status) {
      if (!this.draggingId) return false;
      const task = this.tasks.find((t) => t.id === this.draggingId);
      return task?.status === status;
    },

    setViewMode(mode) {
      this.viewMode = mode;
      localStorage.setItem("tasks.viewMode", mode);
    },

    toggleArchive() {
      this.showArchive = !this.showArchive;
      this.loadAll();
    },

    resetFilters() {
      this.typeFilter = "all";
      this.priorityFilter = "all";
      this.sortMode = "newest";
      this.searchQuery = "";
    },

    // ---- Dialog ----
    openCreateDialog() {
      this.form = this.emptyForm();
      this.editMode = false;
      this.readOnly = false;
      this.submitted = false;
      this.formError = "";
      this.dialog = true;
    },

    openEditDialog(task) {
      this.form = {
        id: task.id,
        type: task.type,
        title: task.title || "",
        description: task.description || "",
        status: task.status,
        priority: task.priority,
        assigneeId: task.assigneeId || null,
        sectionId: task.sectionId || null,
        deadline: task.deadline ? this.toGermanDate(task.deadline) : "",
        author: task.author || null,
        createdAt: task.createdAt,
      };
      this.editMode = true;
      this.readOnly = !this.canEdit(task);
      this.submitted = false;
      this.formError = "";
      this.dialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.assigneeMenuOpen = false;
      this.statusMenuOpen = false;
      this.sectionMenuOpen = false;
      this.sectionSearch = "";
    },

    selectAssignee(emp) {
      this.form.assigneeId = emp ? emp.id : null;
      this.assigneeMenuOpen = false;
    },

    selectSection(section) {
      this.form.sectionId = section ? section.id : null;
      this.sectionMenuOpen = false;
      this.sectionSearch = "";
    },

    selectStatus(value) {
      this.form.status = value;
      this.statusMenuOpen = false;
    },

    handleOutsideClick(e) {
      if (this.assigneeMenuOpen) {
        const el = this.$refs.assigneeDropdown;
        if (el && !el.contains(e.target)) this.assigneeMenuOpen = false;
      }
      if (this.statusMenuOpen) {
        const el = this.$refs.statusDropdown;
        if (el && !el.contains(e.target)) this.statusMenuOpen = false;
      }
      if (this.sectionMenuOpen) {
        const el = this.$refs.sectionDropdown;
        if (el && !el.contains(e.target)) this.sectionMenuOpen = false;
      }
    },

    async saveTask() {
      this.submitted = true;
      this.formError = "";
      if (!this.form.title.trim()) {
        this.formError = "Bitte einen Titel angeben.";
        return;
      }
      this.saving = true;
      try {
        const payload = {
          type: this.form.type,
          title: this.form.title.trim(),
          description: this.form.description || null,
          priority: this.form.priority,
          assigneeId: this.form.assigneeId,
          sectionId: this.form.sectionId,
          deadline: this.form.deadline,
        };
        if (this.editMode) {
          payload.id = this.form.id;
          payload.status = this.form.status;
          payload.requesterId = this.$user?.id || null;
          await axios.post("/api/tasks/update", payload);
        } else {
          payload.authorId = this.$user?.id || null;
          await axios.post("/api/tasks/create", payload);
        }
        await this.loadAll();
        this.closeDialog();
      } catch (e) {
        this.formError = e.response?.data?.message || "Fehler beim Speichern.";
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(task) {
      this.deleteTarget = task;
      this.deleteDialog = true;
    },

    async executeDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      try {
        await axios.post("/api/tasks/delete", {
          id: this.deleteTarget.id,
          requesterId: this.$user?.id || null,
        });
        await this.loadAll();
        this.deleteDialog = false;
        this.deleteTarget = null;
      } catch (e) {
        console.error("Fehler beim Löschen:", e);
      } finally {
        this.deleting = false;
      }
    },

    async unarchiveTask(task) {
      try {
        await axios.post("/api/tasks/unarchive", {
          id: task.id,
          requesterId: this.$user?.id || null,
        });
        // Falls die Task noch auf "done" steht, setzt sie das Backend nicht zurück.
        // Wir bringen sie zurück nach "todo", damit sie nicht direkt wieder auto-archiviert wird.
        await axios.post("/api/tasks/update-status", { id: task.id, status: "todo" });
        await this.loadAll();
      } catch (e) {
        console.error("Fehler beim Wiederherstellen:", e);
      }
    },

    // ---- Drag & Drop ----
    onDragStart(task, ev) {
      this.draggingId = task.id;
      try {
        if (ev?.dataTransfer && ev.currentTarget) {
          ev.dataTransfer.effectAllowed = "move";
          const original = ev.currentTarget;
          const rect = original.getBoundingClientRect();
          this.draggingHeight = Math.round(rect.height);
          // Sauberer Klon, leicht schräg, mit klarer Schatten-Erhebung
          const ghost = original.cloneNode(true);
          ghost.style.position = "fixed";
          ghost.style.top = "-1000px";
          ghost.style.left = "0";
          ghost.style.width = `${rect.width}px`;
          ghost.style.opacity = "1";
          ghost.style.background = "white";
          ghost.style.borderRadius = "8px";
          ghost.style.border = "1px solid #d4d7de";
          ghost.style.boxShadow = "0 4px 12px rgba(10,22,40,0.06), 0 16px 48px rgba(10,22,40,0.16)";
          ghost.style.transform = "rotate(2deg)";
          ghost.style.pointerEvents = "none";
          ghost.classList.remove("task-card-dragging");
          document.body.appendChild(ghost);
          ev.dataTransfer.setDragImage(ghost, 20, 20);
          // Browser hat den Snapshot synchron — wir können den Ghost direkt entfernen
          requestAnimationFrame(() => {
            if (ghost.parentNode) ghost.parentNode.removeChild(ghost);
          });
        }
      } catch (_e) { /* ignore */ }
    },
    onDragEnd() {
      this.draggingId = null;
      this.draggingHeight = 0;
      this.dragOverColumn = null;
    },
    onColumnLeave(e, status) {
      // Vermeiden dass leave bei child-elements feuert
      if (e.currentTarget === e.target && this.dragOverColumn === status) {
        this.dragOverColumn = null;
      }
    },
    async onDrop(targetStatus) {
      if (!this.draggingId) return;
      const task = this.tasks.find((t) => t.id === this.draggingId);
      if (!task || task.status === targetStatus) {
        this.dragOverColumn = null;
        return;
      }
      const previous = task.status;
      task.status = targetStatus;
      this.dragOverColumn = null;
      try {
        await axios.post("/api/tasks/update-status", { id: task.id, status: targetStatus });
        this.showToast({
          type: "status",
          title: task.title,
          fromStatus: previous,
          toStatus: targetStatus,
        });
      } catch (e) {
        console.error("Status-Update fehlgeschlagen:", e);
        task.status = previous;
        this.showToast({
          type: "error",
          message: "Status konnte nicht geändert werden",
        });
      }
    },

    // ---- Toast ----
    showToast(toast) {
      const id = ++this.toastSeq;
      this.toasts.push({ id, ...toast });
      setTimeout(() => this.dismissToast(id), 4000);
    },
    dismissToast(id) {
      const idx = this.toasts.findIndex((t) => t.id === id);
      if (idx >= 0) this.toasts.splice(idx, 1);
    },

    // ---- Helpers ----
    onAvatarError(e) { e.target.style.display = "none"; },
    goToSection(id) {
      this.$router.push(`/section/${id}`);
    },
    ticketNumber(task) {
      const prefix = task.type === "bug" ? "BUG" : "TASK";
      return `${prefix}-${String(task.id).padStart(3, "0")}`;
    },
    canEdit(task) {
      // Nur der Autor darf bearbeiten/löschen/archivieren.
      // Tasks ohne authorId (Legacy) bleiben für jeden bearbeitbar.
      if (!task?.authorId) return true;
      return String(this.$user?.id) === String(task.authorId);
    },
    descriptionPreview(html) {
      if (!html) return "";
      // HTML-Tags entfernen, Whitespace zusammenfassen, max 200 Zeichen
      const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      return text.length > 200 ? text.substring(0, 200) + "…" : text;
    },
    initials(emp) {
      if (!emp) return "?";
      return (emp.firstname?.charAt(0) || "") + (emp.lastname?.charAt(0) || "");
    },
    statusLabel(value) { return STATUSES.find((s) => s.value === value)?.label || value; },
    statusColorOf(value) { return STATUSES.find((s) => s.value === value)?.dotColor || "#888"; },
    statusStyle(value) {
      const c = this.statusColorOf(value);
      return { background: `${c}1a`, color: c, borderColor: `${c}33` };
    },
    priorityLabel(value) { return PRIORITIES.find((p) => p.value === value)?.label || value; },
    priorityIcon(value) { return PRIORITIES.find((p) => p.value === value)?.icon || "mdi-minus"; },
    isOverdue(date) {
      if (!date) return false;
      const d = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d < today;
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    toGermanDate(isoDate) {
      // Backend liefert YYYY-MM-DD oder ISO mit Zeit
      const d = new Date(isoDate);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      return `${dd}.${mm}.${d.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.fade-in { animation: fadeIn 0.4s ease-out; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }

.page-title {
  font-size: 26px; font-weight: 800; color: #0a1628;
  letter-spacing: -0.3px; line-height: 1.1; margin: 0;
}

/* Buttons */
.bp {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }
.bp.btn-danger { background: #D32F2F; }
.bp.btn-danger:hover:not(:disabled) { background: #b71c1c; }

.bo {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 16px; background: white; color: #0a1628;
  border: 1px solid #eceef2; border-radius: 10px; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.4; cursor: not-allowed; }
.bo.bo-active { background: #0a1628; color: white; border-color: #0a1628; }

.btn-text-danger {
  background: transparent; border: none; color: #D32F2F;
  font-size: 12px; font-weight: 600; cursor: pointer; padding: 6px 10px;
}
.btn-text-danger:hover { text-decoration: underline; }

/* Toolbar */
.toolbar { gap: 12px; flex-wrap: wrap; }
.filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.search-wrap {
  position: relative; display: flex; align-items: center;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  padding: 0 12px 0 38px; height: 40px; width: 280px;
  transition: border-color 0.15s;
}
.search-wrap:focus-within { border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.search-icon { position: absolute; left: 12px; }
.search-clear { position: absolute; right: 10px; cursor: pointer; }
.search-input {
  border: none; outline: none; font-size: 13px; color: #0a1628;
  width: 100%; background: transparent; font-family: inherit;
}

/* View toggle */
.view-toggle {
  display: inline-flex; padding: 4px;
  background: white; border: 1px solid #eceef2; border-radius: 10px; height: 40px;
}
.view-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0 14px; background: transparent; border: none;
  font-size: 12px; font-weight: 600; color: #888;
  cursor: pointer; border-radius: 6px; transition: all 0.15s; font-family: inherit;
}
.view-btn-active { background: #0a1628; color: white; }

/* === Kanban Board === */
.kanban-board {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  align-items: start;
}

/* Easing-Variable für ein einheitliches, sauberes Gefühl */
.kanban-board, .kanban-column, .task-card, .toast, .drop-slot {
  --ease: cubic-bezier(0.32, 0.72, 0, 1);
}

.kanban-column {
  background: #fafbfc;
  border: 1px solid #eceef2;
  border-radius: 12px;
  padding: 8px 8px 10px;
  min-height: 240px;
  transition: background 0.22s var(--ease), border-color 0.22s var(--ease);
}
.kanban-column.drag-over {
  background: #f3f4f8;
  border-color: #d4d7de;
}

.column-header {
  padding: 6px 6px 10px;
  display: flex; align-items: center;
}
.column-title { font-size: 13px; font-weight: 600; color: #0a1628; }
.count-chip {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 6px;
  background: #eceef2; color: #6b7280;
  border-radius: 9px; font-size: 10px; font-weight: 700;
  margin-left: 6px;
}
.status-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; display: inline-block; }

.cards-container { display: flex; flex-direction: column; gap: 6px; min-height: 80px; }

/* === Task Card === */
.task-card {
  background: white;
  border: 1px solid #eceef2;
  /* Asymmetrisch: links kantig, rechts gerundet */
  border-radius: 0 10px 10px 0;
  padding: 12px 14px 10px 14px;
  cursor: grab;
  transition: box-shadow 0.22s var(--ease), border-color 0.22s var(--ease), opacity 0.22s var(--ease);
  user-select: none;
  position: relative;
}
.task-card:hover {
  border-color: #d4d7de;
  box-shadow: 0 1px 2px rgba(10,22,40,0.04), 0 4px 14px rgba(10,22,40,0.05);
}
.task-card:active { cursor: grabbing; }

/* Linkskante – farbiger Akzent je nach Typ */
.task-card::before {
  content: ""; position: absolute;
  top: 0; bottom: 0; left: 0;
  width: 3px;
  background: #5B5BD6;
}
.task-card-bug::before { background: #D32F2F; }

.task-card-dragging {
  opacity: 0.4;
  cursor: grabbing;
}

/* === Card Top: Ticket-Badge, Type, Priority === */
.task-card-top {
  display: flex; align-items: center; gap: 6px;
  margin-bottom: 8px;
}
.task-ticket-badge {
  display: inline-flex; align-items: center;
  padding: 2px 7px;
  background: #f0f2f5;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 10.5px; font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.2px;
}
.task-type-tag {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px; font-weight: 600;
}
.task-type-tag.type-task { color: #5B5BD6; background: rgba(91,91,214,0.08); }
.task-type-tag.type-bug  { color: #D32F2F; background: rgba(211,47,47,0.08); }

.task-prio-icon {
  display: inline-flex; align-items: center; justify-content: center;
  margin-left: auto;
  width: 20px; height: 20px; border-radius: 5px;
}
.task-prio-icon.prio-low    { color: #9ca3af; }
.task-prio-icon.prio-medium { color: #3b82f6; }
.task-prio-icon.prio-high   { color: #f59e0b; }
.task-prio-icon.prio-urgent { color: #dc2626; background: rgba(220,38,38,0.1); }

/* === Title + Description === */
.task-card-title {
  font-size: 14.5px; font-weight: 700; color: #0a1628;
  line-height: 1.35; margin: 0 0 5px 0;
  letter-spacing: -0.15px;
  /* 2 Zeilen, dann ... */
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
}
.task-card-desc {
  font-size: 12px; color: #8a8e96; line-height: 1.5;
  margin: 0 0 10px 0;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Section-Referenz */
.task-section-ref {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 7px; margin-bottom: 8px;
  background: #f3f4f8;
  border-radius: 5px;
  font-size: 11px; font-weight: 500; color: #4b5563;
  cursor: pointer; transition: background 0.18s var(--ease);
  text-decoration: none;
  max-width: 100%;
}
.task-section-ref:hover { background: #e9eaf0; color: #0a1628; }
.task-section-thumb {
  width: 14px; height: 14px; border-radius: 3px; object-fit: cover; flex-shrink: 0;
  background: #eceef2;
}
.task-section-thumb-empty {
  display: inline-flex; align-items: center; justify-content: center;
}
.task-section-title {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Deadline (eigene Zeile über Divider) */
.task-deadline {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: #8a8e96; font-weight: 500;
  margin-bottom: 8px;
}
.task-deadline-overdue { color: #D32F2F; font-weight: 600; }

/* Divider zwischen Body und Footer */
.task-card-divider {
  height: 1px;
  background: #eceef2;
  margin: 0 -14px 8px -14px;
}

/* === Footer mit Assignee === */
.task-card-foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px;
  min-height: 22px;
}
.task-assignee-row {
  display: flex; align-items: center; gap: 7px;
  min-width: 0; flex: 1;
}
.task-assignee-name {
  font-size: 12px; font-weight: 500; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.task-assignee-empty .task-assignee-name { color: #9ca3af; font-style: italic; font-weight: 400; }

.avatar-sm {
  width: 22px; height: 22px; border-radius: 50%; object-fit: cover;
  border: 1.5px solid white; box-shadow: 0 0 0 1px #eceef2;
  flex-shrink: 0;
}
.avatar-fallback {
  background: #0a1628; color: white;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700;
}
.avatar-empty {
  background: #f0f2f5; color: #c5cbd3;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1.5px solid white; box-shadow: 0 0 0 1px #eceef2;
}

.task-card-actions {
  display: flex; gap: 2px;
  opacity: 0;
  transform: translateX(4px);
  transition: opacity 0.18s var(--ease), transform 0.18s var(--ease);
}
.task-card:hover .task-card-actions {
  opacity: 1;
  transform: translateX(0);
}
.task-icon-btn {
  width: 24px; height: 24px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 5px;
  color: #9ca3af; cursor: pointer;
  transition: all 0.15s var(--ease);
}
.task-icon-btn:hover { background: #f0f2f5; color: #0a1628; }
.task-icon-btn-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }

/* === Drop-Slot (dezent, am Ende der Ziel-Spalte) === */
/* Höhe wird inline gesetzt → matched die ziehende Karte */
.drop-slot {
  border-radius: 0 10px 10px 0;
  background: #f0f2f5;
  border: 1.5px dashed #c5cbd3;
  animation: dropSlotIn 0.18s var(--ease);
}
@keyframes dropSlotIn {
  from { opacity: 0; transform: translateY(-2px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* Empty State */
.empty-column {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 32px 12px; text-align: center; gap: 6px;
  font-size: 11px; color: #c5cbd3;
}

/* Skeleton */
.skel-card {
  height: 110px; border-radius: 12px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

/* === List View === */
.list-view {
  background: white;
  border: 1px solid #eceef2;
  border-radius: 12px;
  overflow: hidden;
}
.tasks-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}
.tasks-table th {
  font-size: 10.5px; font-weight: 700; color: #9ca3af;
  text-transform: uppercase; letter-spacing: 0.5px;
  background: #fafbfc;
  padding: 10px 14px; text-align: left;
  border-bottom: 1px solid #eceef2;
  white-space: nowrap;
}
.tasks-table td {
  padding: 12px 14px; vertical-align: middle;
  border-bottom: 1px solid #f0f2f5;
}
.tasks-table tr:last-child td { border-bottom: none; }

.th-id        { width: 160px; }
.th-title     { width: auto; }
.th-assignee  { width: 180px; }
.th-status    { width: 150px; }
.th-prio      { width: 110px; }
.th-date      { width: 120px; }
.th-actions   { width: 90px; }

.tasks-table-row {
  cursor: pointer;
  transition: background 0.12s var(--ease);
  position: relative;
}
.tasks-table-row:hover { background: #fafbfc; }
.tasks-table-row-bug td:first-child { border-left: 2px solid #D32F2F; }
.tasks-table-row td:first-child { border-left: 2px solid transparent; }

.row-id-cell {
  display: flex; flex-direction: column; gap: 4px;
  align-items: flex-start;
}

.row-title {
  font-size: 13px; font-weight: 600; color: #0a1628;
  line-height: 1.35; letter-spacing: -0.1px;
  overflow: hidden; text-overflow: ellipsis;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
}
.row-section-ref {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 4px; padding: 2px 7px;
  background: #f3f4f8;
  border-radius: 5px;
  font-size: 11px; font-weight: 500; color: #5B5BD6;
  cursor: pointer; transition: background 0.15s;
  max-width: 100%;
}
.row-section-ref:hover { background: rgba(91,91,214,0.12); color: #0a1628; }
.row-section-thumb {
  width: 14px; height: 14px; border-radius: 3px; object-fit: cover; flex-shrink: 0;
  background: #eceef2;
}
.row-section-ref span {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.row-assignee {
  display: inline-flex; align-items: center; gap: 8px;
  min-width: 0; max-width: 100%;
}
.row-assignee-name {
  font-size: 12px; font-weight: 500; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.row-status-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
  border: 1px solid;
  white-space: nowrap;
}

.row-prio {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 8px; border-radius: 999px;
  font-size: 11px; font-weight: 700;
  white-space: nowrap;
}
.row-prio.prio-low    { background: rgba(107,114,128,0.1); color: #6b7280; }
.row-prio.prio-medium { background: rgba(59,130,246,0.1);  color: #3b82f6; }
.row-prio.prio-high   { background: rgba(245,158,11,0.1);  color: #f59e0b; }
.row-prio.prio-urgent { background: rgba(220,38,38,0.1);   color: #dc2626; }

.row-date {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; color: #6b7280; font-weight: 500;
  white-space: nowrap;
}
.row-date-overdue { color: #D32F2F; font-weight: 600; }
.row-date-muted   { color: #9ca3af; font-weight: 400; }

.row-muted { color: #c5cbd3; font-size: 13px; }

.row-actions {
  display: flex; gap: 4px;
  justify-content: flex-end;
  opacity: 0;
  transition: opacity 0.15s var(--ease);
}
.tasks-table-row:hover .row-actions { opacity: 1; }

.row-empty {
  text-align: center !important;
  padding: 40px 16px !important;
  color: #c5cbd3;
  font-size: 13px;
}

/* Dialog */
.dialog-backdrop {
  position: fixed; inset: 0; background: rgba(10,22,40,0.4); z-index: 1000;
  animation: fadeIn 0.15s ease;
}
.modern-dialog {
  background: white !important;
  position: relative;
  padding: 0; overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 88vh;
}
.close-btn {
  position: absolute; top: 16px; right: 16px;
  width: 32px; height: 32px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px;
  color: #888; cursor: pointer; z-index: 1;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; }

.dh {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 24px 28px 8px;
}
.dh-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(10,22,40,0.06);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 800; color: #0a1628; }
.dh-sub { font-size: 13px; color: #888; margin-top: 2px; }

.db {
  padding: 16px 28px;
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}
/* Read-Only-Modus: Form-Elemente nicht klickbar/editierbar */
.db-readonly button:not(.combo-clear),
.db-readonly .iw-trigger,
.db-readonly .text-input,
.db-readonly .priority-option,
.db-readonly .type-toggle-btn {
  pointer-events: none;
}
.db-readonly .text-input { background: #fafbfc; color: #4b5563; }
.db-readonly .iw-trigger { background: #fafbfc !important; color: #4b5563; }
.db-readonly .iw-trigger .chev-open,
.db-readonly .iw-trigger .v-icon { opacity: 0.5; }
.db-readonly :deep(.ql-toolbar) { display: none !important; }
.db-readonly :deep(.ql-container) { border-top: none !important; }
.db-readonly :deep(.ql-editor) { background: #fafbfc; color: #4b5563; }
.dff {
  display: flex; gap: 10px; padding: 14px 28px 18px;
  border-top: 1px solid #eceef2;
  background: white;
  flex: 0 0 auto;
  box-shadow: 0 -4px 12px rgba(10, 22, 40, 0.04);
}
.dff button { flex: 1; }

/* Form */
.field-block { margin-bottom: 14px; }
.field-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
.field-row-2 .field-block { margin-bottom: 0; }
.field-label { display: block; font-size: 12px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }

.text-input {
  width: 100%; padding: 10px 12px;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  font-size: 13px; color: #0a1628; font-family: inherit;
  transition: border-color 0.15s;
}
.text-input:focus { outline: none; border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.text-input.input-error { border-color: #D32F2F; }
textarea.text-input { resize: vertical; min-height: 80px; line-height: 1.5; }

.alert-error {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 12px; background: rgba(211,47,47,0.05); color: #D32F2F;
  border: 1px solid rgba(211,47,47,0.2); border-radius: 8px;
  font-size: 12px; font-weight: 500;
}

/* Rich-Text-Editor (Quill) im Floow-Look */
.rich-editor-wrap {
  border: 1px solid #eceef2;
  border-radius: 10px;
  overflow: hidden;
  background: white;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.rich-editor-wrap:focus-within {
  border-color: #0a1628;
  box-shadow: 0 0 0 3px rgba(10,22,40,0.04);
}
.rich-editor-wrap :deep(.ql-toolbar) {
  border: none !important;
  border-bottom: 1px solid #eceef2 !important;
  background: #fafbfc;
  padding: 6px 8px;
}
.rich-editor-wrap :deep(.ql-container) {
  border: none !important;
  font-family: inherit;
  font-size: 13px;
}
.rich-editor-wrap :deep(.ql-editor) {
  min-height: 140px;
  color: #0a1628;
  padding: 12px 14px;
  line-height: 1.55;
}
.rich-editor-wrap :deep(.ql-editor.ql-blank::before) {
  color: #c5cbd3;
  font-style: normal;
  font-size: 13px;
  left: 14px;
  right: 14px;
}
.rich-editor-wrap :deep(.ql-editor h1) { font-size: 22px; font-weight: 800; color: #0a1628; margin: 0.4em 0; }
.rich-editor-wrap :deep(.ql-editor h2) { font-size: 18px; font-weight: 700; color: #0a1628; margin: 0.4em 0; }
.rich-editor-wrap :deep(.ql-editor h3) { font-size: 16px; font-weight: 700; color: #0a1628; margin: 0.4em 0; }
.rich-editor-wrap :deep(.ql-editor a) { color: #5B5BD6; }
.rich-editor-wrap :deep(.ql-editor pre.ql-syntax) {
  background: #0a1628; color: #f8f9fb;
  border-radius: 8px; padding: 10px 12px; font-size: 12px;
}
.rich-editor-wrap :deep(.ql-editor blockquote) {
  border-left: 3px solid #5B5BD6;
  padding-left: 12px; color: #444; margin: 0.4em 0;
}

/* Read-Only Content (kein Quill-Editor, statisches HTML-Rendering) */
.readonly-content {
  background: #fafbfc;
  border: 1px solid #eceef2;
  border-radius: 10px;
  padding: 14px 16px;
  min-height: 80px;
  font-size: 13px;
  color: #0a1628;
  line-height: 1.55;
}
.readonly-content :deep(p) { margin: 0.3em 0; }
.readonly-content :deep(p:first-child) { margin-top: 0; }
.readonly-content :deep(p:last-child) { margin-bottom: 0; }
.readonly-content :deep(h1) { font-size: 20px; font-weight: 800; color: #0a1628; margin: 0.5em 0 0.3em; }
.readonly-content :deep(h2) { font-size: 17px; font-weight: 700; color: #0a1628; margin: 0.5em 0 0.3em; }
.readonly-content :deep(h3) { font-size: 15px; font-weight: 700; color: #0a1628; margin: 0.5em 0 0.3em; }
.readonly-content :deep(a) { color: #5B5BD6; }
.readonly-content :deep(ul), .readonly-content :deep(ol) { padding-left: 1.5em; margin: 0.3em 0; }
.readonly-content :deep(blockquote) {
  border-left: 3px solid #5B5BD6;
  padding-left: 12px; color: #444; margin: 0.4em 0;
}
.readonly-content :deep(pre) {
  background: #0a1628; color: #f8f9fb;
  border-radius: 8px; padding: 10px 12px; font-size: 12px;
  overflow-x: auto;
}
.readonly-content :deep(code) {
  background: #f0f2f5; padding: 1px 5px; border-radius: 4px;
  font-family: 'SFMono-Regular', Menlo, monospace; font-size: 12px;
}
.readonly-empty { color: #9ca3af; font-style: italic; font-size: 12px; }

/* Type Toggle */
.type-toggle {
  display: inline-flex; padding: 4px;
  background: #fafbfc; border: 1px solid #eceef2; border-radius: 10px;
}
.type-toggle-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 16px; background: transparent; border: none;
  font-size: 12px; font-weight: 700; color: #888;
  cursor: pointer; border-radius: 7px; transition: all 0.15s; font-family: inherit;
}
.type-toggle-btn.type-active { background: #5B5BD6; color: white; }
.type-toggle-btn.type-active-bug { background: #D32F2F; color: white; }

/* Priority grid */
.priority-grid {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 6px;
}
.priority-option {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 10px; background: white;
  border: 1px solid #eceef2; border-radius: 8px;
  font-size: 11px; font-weight: 600; color: #888;
  cursor: pointer; transition: all 0.15s; font-family: inherit;
}
.priority-option:hover { border-color: #c5cbd3; }
.priority-option.priority-active { font-weight: 700; }

/* Custom dropdown (combo) */
.combo-wrap { position: relative; }
.iw {
  display: flex; align-items: center; gap: 8px;
  width: 100%; padding: 10px 12px;
  background: white !important; border: 1px solid #eceef2; border-radius: 10px;
  font-size: 13px; color: #0a1628; font-family: inherit; cursor: pointer;
  transition: border-color 0.15s;
  text-align: left;
}
.iw:hover { border-color: #c5cbd3; }
.iw.iw-f { border-color: #0a1628; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.iw-trigger { height: 42px; }
.ii-val { flex: 1; font-size: 13px; color: #0a1628; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chev-open { transform: rotate(180deg); transition: transform 0.2s; }

.combo-menu {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #eceef2; border-radius: 10px;
  box-shadow: 0 12px 32px rgba(10,22,40,0.12);
  z-index: 50; max-height: 280px; overflow-y: auto;
  padding: 6px;
  animation: comboFade 0.12s ease;
}
@keyframes comboFade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
.combo-option {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 8px 10px; background: transparent; border: none;
  border-radius: 7px; cursor: pointer; transition: background 0.1s;
  text-align: left; font-family: inherit;
  font-size: 13px;
}
.combo-option:hover { background: #fafbfc; }
.combo-label { flex: 1; color: #0a1628; font-weight: 500; }
.combo-divider { height: 1px; background: #eceef2; margin: 4px 0; }
.combo-avatar { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }
.combo-avatar-fallback {
  width: 22px; height: 22px; border-radius: 50%;
  background: #0a1628; color: white;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700;
}

.trigger-avatar { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }
.trigger-avatar-fallback {
  width: 22px; height: 22px; border-radius: 50%;
  background: #0a1628; color: white;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700;
}

.meta-info {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 12px; background: #fafbfc; border: 1px solid #eceef2;
  border-radius: 8px; font-size: 12px; color: #888;
  margin-bottom: 6px;
}

/* Section-Referenz auf Karte */
.section-ref {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 8px; margin-bottom: 10px;
  background: rgba(91,91,214,0.06);
  border: 1px solid rgba(91,91,214,0.15);
  border-radius: 8px;
  font-size: 11px; font-weight: 600; color: #5B5BD6;
  cursor: pointer; transition: all 0.15s;
  max-width: 100%;
  text-decoration: none;
}
.section-ref:hover {
  background: rgba(91,91,214,0.1);
  border-color: rgba(91,91,214,0.3);
}
.section-ref-thumb {
  width: 18px; height: 18px; border-radius: 4px; object-fit: cover; flex-shrink: 0;
  background: #f0f2f5;
}
.section-ref-thumb-empty {
  display: inline-flex; align-items: center; justify-content: center;
}
.section-ref-title {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Section-Referenz in Listen-View */
.row-section-ref {
  display: inline-flex; align-items: center; gap: 4px;
  margin-top: 3px; padding: 2px 6px;
  background: rgba(91,91,214,0.06); color: #5B5BD6;
  border-radius: 6px; font-size: 11px; font-weight: 600;
  cursor: pointer; transition: background 0.15s;
}
.row-section-ref:hover { background: rgba(91,91,214,0.12); }

/* Section-Trigger im Dropdown */
.trigger-section-thumb {
  width: 24px; height: 24px; border-radius: 5px; object-fit: cover;
  background: #f0f2f5; flex-shrink: 0;
}
.trigger-section-thumb-empty {
  display: inline-flex; align-items: center; justify-content: center;
}
.trigger-section-title {
  display: block; font-weight: 600; color: #0a1628;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font-size: 13px; line-height: 1.2;
}
.trigger-section-handle {
  display: block; font-size: 10px; color: #888;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
}

.combo-clear {
  width: 24px; height: 24px; padding: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 6px;
  color: #888; cursor: pointer;
}
.combo-clear:hover { background: #f0f2f5; color: #D32F2F; }

.combo-menu-section { max-height: 360px; }
.combo-search {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 8px;
  background: #fafbfc; border-radius: 8px;
}
.combo-search-input {
  flex: 1; border: none; background: transparent;
  font-size: 13px; color: #0a1628; outline: none; font-family: inherit;
}
.combo-option-section { padding: 6px 8px !important; }
.combo-section-thumb {
  width: 32px; height: 32px; border-radius: 6px; object-fit: cover;
  background: #f0f2f5; flex-shrink: 0;
}
.combo-section-thumb-empty {
  display: inline-flex; align-items: center; justify-content: center;
}
.combo-section-text { flex: 1; min-width: 0; }
.combo-section-text .combo-label { font-size: 13px; }
.combo-handle {
  font-size: 10px; color: #888;
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 1px;
}
.combo-empty { padding: 12px 14px; font-size: 13px; color: #999; text-align: center; }

/* === Toast === */
.toast-stack {
  position: fixed;
  bottom: 22px;
  right: 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 2000;
  pointer-events: none;
}
.toast {
  display: flex; align-items: flex-start; gap: 11px;
  min-width: 300px; max-width: 380px;
  padding: 12px 36px 12px 14px;
  background: white;
  border: 1px solid #eceef2;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(10,22,40,0.04), 0 8px 24px rgba(10,22,40,0.08);
  cursor: pointer;
  pointer-events: auto;
  position: relative;
}
.toast-icon {
  width: 28px; height: 28px;
  flex-shrink: 0;
  border-radius: 7px;
  background: rgba(10,22,40,0.92);
  display: flex; align-items: center; justify-content: center;
}
.toast-icon-error { background: #D32F2F; }
.toast-body { flex: 1; min-width: 0; }
.toast-title { font-size: 12px; font-weight: 700; color: #0a1628; margin-bottom: 2px; letter-spacing: -0.05px; }
.toast-task-title {
  font-size: 11px; color: #6b7280; margin-bottom: 6px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.toast-flow {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600;
}
.toast-status {
  display: inline-flex; align-items: center; gap: 4px;
  white-space: nowrap;
}
.toast-status .status-dot { width: 6px; height: 6px; }
.toast-close {
  position: absolute; top: 8px; right: 8px;
  width: 20px; height: 20px; padding: 0;
  background: transparent; border: none; border-radius: 4px;
  color: #c5cbd3; cursor: pointer;
  transition: all 0.15s var(--ease);
}
.toast-close:hover { background: #f0f2f5; color: #6b7280; }

/* Toast-Animation: clean ease-out, kein Bounce */
.toast-enter-active { transition: opacity 0.24s var(--ease), transform 0.32s var(--ease); }
.toast-leave-active { transition: opacity 0.18s ease-in, transform 0.18s ease-in; }
.toast-enter-from { opacity: 0; transform: translateX(20px); }
.toast-leave-to   { opacity: 0; transform: translateX(20px); }
.toast-move { transition: transform 0.32s var(--ease); }

@media (max-width: 1200px) {
  .kanban-board { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 768px) {
  .kanban-board { grid-template-columns: 1fr; }
  .field-row-2 { grid-template-columns: 1fr; }
}
</style>
